export const colorsBg = {
  white: 'bg-white text-gray-800 dark:bg-gray-800 dark:text-gray-100',
  light: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100',
  success: 'bg-green-600 text-white',
  danger: 'bg-red-600 text-white',
  /*
   * amber-700, not amber-500: white on amber-500 is 2.2:1, well under WCAG AA
   * for a label — and these are the Pause buttons on campaigns. White on
   * amber-700 is 5.0:1. The hover, ring, text and outline tones below moved
   * with it.
   */
  warning: 'bg-amber-700 text-white',
  /*
   * Teal brand-600: white on it is 4.77:1 (AA). The fill measures 3.2:1
   * against the gray-800 cards of the dark theme, so unlike the old plum 950
   * fill it needs no border there to read as a button.
   */
  info: 'bg-brand-600 text-white',
  brand: 'bg-brand-600 text-white',
  accent: 'bg-accent-600 text-white',
}

export const colorsBgHover = {
  white: 'hover:bg-gray-50 hover:border-gray-300 dark:hover:bg-gray-700',
  light: 'hover:bg-gray-200 dark:hover:bg-gray-700',
  success: 'hover:bg-green-700',
  danger: 'hover:bg-red-700',
  warning: 'hover:bg-amber-800',
  info: 'hover:bg-brand-700',
  brand: 'hover:bg-brand-700',
  accent: 'hover:bg-accent-700',
}

export const colorsBorders = {
  white: 'border-gray-200 dark:border-gray-700',
  light: 'border-gray-200 dark:border-gray-700',
  success: 'border-transparent',
  danger: 'border-transparent',
  warning: 'border-transparent',
  info: 'border-transparent',
  brand: 'border-transparent',
  accent: 'border-transparent',
}

/* Focus ring per colour — keeps keyboard focus visible without the old harsh blue. */
export const colorsFocusRing = {
  white: 'focus-visible:ring-gray-400',
  light: 'focus-visible:ring-gray-400',
  success: 'focus-visible:ring-green-500',
  danger: 'focus-visible:ring-red-500',
  // 600: the first amber that clears 3:1 against a white page (amber-400 was 1.7:1).
  warning: 'focus-visible:ring-amber-600',
  info: 'focus-visible:ring-brand-500',
  brand: 'focus-visible:ring-brand-500',
  accent: 'focus-visible:ring-accent-500',
}

/* Solid buttons sit on a faint neutral shadow; neutral buttons stay flat.
   Hover changes colour only (colorsBgHover). The shadow no longer grows,
   because a growing shadow reads as the button lifting. */
export const colorsShadow = {
  white: '',
  light: '',
  success: 'shadow-sm',
  danger: 'shadow-sm',
  warning: 'shadow-sm',
  info: 'shadow-sm',
  brand: 'shadow-sm',
  accent: 'shadow-sm',
}

export const colorsText = {
  white: 'text-gray-800 dark:text-gray-100',
  light: 'text-gray-700 dark:text-gray-300',
  success: 'text-green-600 dark:text-green-400',
  danger: 'text-red-600 dark:text-red-400',
  warning: 'text-amber-700 dark:text-amber-400',
  info: 'text-brand-600 dark:text-brand-400',
  brand: 'text-brand-600 dark:text-brand-400',
  accent: 'text-accent-600 dark:text-accent-400',
}

/* Outlined buttons keep a visible border, so they can't share the solid set's
   `border-transparent`. */
export const colorsOutlineBorders = {
  white: 'border-gray-300 dark:border-gray-700',
  light: 'border-gray-200 dark:border-gray-700',
  success: 'border-green-500',
  danger: 'border-red-500',
  warning: 'border-amber-600',
  info: 'border-brand-500',
  brand: 'border-brand-500',
  accent: 'border-accent-500',
}

export const colorsOutline = {
  white: [colorsText.white, colorsOutlineBorders.white],
  light: [colorsText.light, colorsOutlineBorders.light],
  success: [colorsText.success, colorsOutlineBorders.success],
  danger: [colorsText.danger, colorsOutlineBorders.danger],
  warning: [colorsText.warning, colorsOutlineBorders.warning],
  info: [colorsText.info, colorsOutlineBorders.info],
  brand: [colorsText.brand, colorsOutlineBorders.brand],
  accent: [colorsText.accent, colorsOutlineBorders.accent],
}

export const colorsOutlineHover = {
  white: 'hover:bg-gray-50 dark:hover:bg-gray-700',
  light: 'hover:bg-gray-50 dark:hover:bg-gray-700',
  success: 'hover:bg-green-600 hover:text-white hover:border-transparent',
  danger: 'hover:bg-red-600 hover:text-white hover:border-transparent',
  warning: 'hover:bg-amber-700 hover:text-white hover:border-transparent',
  info: 'hover:bg-brand-600 hover:text-white hover:border-transparent',
  brand: 'hover:bg-brand-600 hover:text-white hover:border-transparent',
  accent: 'hover:bg-accent-600 hover:text-white hover:border-transparent',
}

export const getButtonColor = (color, isOutlined, hasHover) => {
  const key = colorsBg[color] ? color : 'white'

  const base = [
    isOutlined ? colorsText[key] : colorsBg[key],
    isOutlined ? colorsOutlineBorders[key] : colorsBorders[key],
    colorsFocusRing[key]
  ]

  if (!isOutlined) {
    base.push(colorsShadow[key])
  }

  if (hasHover) {
    base.push(isOutlined ? colorsOutlineHover[key] : colorsBgHover[key])
  }

  return base
}
